/** The filings that were issued from an inventory with this document's figures in it (EXP-19).
 *
 * "Report usage" used to mean which filing *would* state a document's figures, because nothing
 * could say more. An artifact is issued, hashed and kept since ADR-0020, so the server now names the
 * ones built from these figures (`FilingUse.issued`), and this draws them. It says only what the
 * server says: **built from an inventory that included the figures**, not "stated on line N" - a
 * whole record is not enumerated in a filing, and `cites_exactly` marks the exception. A figure
 * restated since is counted, because an issued filing keeps the figure it was built with.
 *
 * Nothing here is derived in the browser; the digest is shown short and in full on the filing.
 */

import { Link } from "@tanstack/react-router";

import { useReader } from "../api/profile";
import type { Usage } from "../api/evidence";
import { instantDate } from "../lib/format";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import styles from "./Evidence.module.css";

type Filing = Usage["filings"][number];

export function UsageIssued({ filings }: { filings: readonly Filing[] }) {
  const reader = useReader();
  const issued = filings.flatMap((filing) => filing.issued.map((use) => ({ filing, use })));
  if (issued.length === 0) return null;
  return (
    <div data-testid="usage-issued" style={{ marginTop: 10 }}>
      {issued.map(({ filing, use }) => (
        <div key={use.artifact_id} className={styles.usageRow} data-testid="usage-issued-row">
          <span className={styles.usageMain}>
            {filing.regime_key.replace(/_/g, " ")} · {filing.legal_entity_name}
            <span className={styles.usageSub}>
              Issued {instantDate(use.issued_at, reader)} · {use.checks_verdict.replace(/_/g, " ")} ·{" "}
              {use.scope2_method} basis · digest {use.sha256.slice(0, 12)}
            </span>
          </span>
          <span className={styles.usageFigures}>
            <span data-testid="usage-issued-count">
              {use.figures} figure{use.figures === 1 ? "" : "s"} in it
              {use.restated_since > 0 ? `, ${String(use.restated_since)} restated since` : ""}
              {use.cites_exactly ? ", named in the filing" : ""}
            </span>{" "}
            <Link
              className={styles.fixLink}
              to="/reports"
              search={(prev: Partial<ScreenSearch>) => ({
                ...keepSearch(prev),
                filing: filing.obligation_id,
              })}
            >
              Open filing
            </Link>
          </span>
        </div>
      ))}
    </div>
  );
}
